import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSystem } from '@ohif/core';
import { Formik, Form } from 'formik';
import PageLayout from '../../components/layouts/PageLayout';
import '../../theme/common.css';
import {
  FormField,
  FormSelect,
  FormMultiSelect,
  FormPhoneInput,
  FormSection,
  FormButtonGroup,
  FormRadioGroup,
  FormTimePicker,
  FormDatePicker,
  FileUpload,
} from '../../components/common';
import { Icons } from '@ohif/ui-next';
import { authService } from '../../auth/authService';
import userService from '../../services/userService';
import { mapFormValuesToCreateRequest, mapApiResponseToFormValues } from '../../utils/userMapper';
import { getUserValidationSchema } from '../../validation';
import { trimFormStringFields } from '../../utils/trimFormStringFields';
import {
  GENDER_OPTIONS,
  MODALITY_OPTIONS,
  isAllowedSignatureUploadFile,
  SIGNATURE_UPLOAD_ACCEPT,
  SIGNATURE_UPLOAD_TYPE_ERROR,
} from '../../constants/constant';
import { getModalities } from '../../utils/modalityUtils';
import type { User } from '../../types/user.types';
import {
  navigateAfterUserSave,
  notifyUserForm,
  resolveAddUserFacilityId,
  resolveCurrentUserRoleForMapper,
  validateRadiologistSignatureUpload,
} from './addUserSubmitHelpers';

function withoutAvailabilityDay(days: string[], dayValue: string): string[] {
  const next: string[] = [];
  for (const d of days) {
    if (d !== dayValue) {
      next.push(d);
    }
  }
  return next;
}

interface LocationState {
  user?: User | unknown;
  isEdit?: boolean;
  facilityId?: string;
  facilityName?: string;
}

// Helper function to map user data to form initial values
// Password placeholder to show in edit mode
const PASSWORD_PLACEHOLDER = '••••••';

const mapUserToInitialValues = (user: User | null | undefined, role?: string) => {
  if (!user) {
    return {
      role: role || '',
      sub_role: '',
      employeeId: '',
      fullName: '',
      gender: '',
      dateOfBirth: '',
      username: '',
      email: '',
      phoneNo: '',
      password: '',
      status: 'active',
      qualification: '',
      experienceYears: undefined as number | undefined,
      // Technician fields
      modality: [] as string[],
      shiftStart: '',
      shiftEnd: '',
      // Radiologist fields
      registrationNo: '',
      experience: '',
      specialty: '',
      designation: '',
      reportingModalityAccess: [] as string[],
      signature: null as File | null,
      signatureFileName: '',
      availabilityHours: [] as string[],
      availabilityDays: [] as string[],
      isEdit: false,
    };
  }

  const mapped = mapApiResponseToFormValues(user);
  const reportingModalityAccess = mapped.reportingModalityAccess || [];

  return {
    ...mapped,
    sub_role: mapped.sub_role || '',
    // Show password placeholder in edit mode
    password: PASSWORD_PLACEHOLDER,
    // Technician fields
    shiftStart: mapped.shiftStart || '',
    shiftEnd: mapped.shiftEnd || '',
    experienceYears: mapped.experienceYears, // Populate experience_years from API (for both Technician and Radiologist)
    // Radiologist fields - map from API response
    registrationNo: mapped.registrationNumber || '',
    experience: '', // This field might not be in API response, keep empty for now
    specialty: mapped.specialty || '',
    designation: mapped.designation || '',
    reportingModalityAccess: reportingModalityAccess,
    signature: null as File | null,
    signatureFileName: mapped.signatureFileName || '',
    // Map availability hours and days from API response
    availabilityHours: mapped.availabilityHours || [],
    availabilityDays: mapped.availabilityDays || [],
    isEdit: true,
  };
};

type AddUserFormValues = ReturnType<typeof mapUserToInitialValues>;

const AddUser: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { servicesManager } = useSystem();
  const { uiNotificationService } = servicesManager.services;
  const locationState = location.state as LocationState | null;
  const userData = locationState?.user as User | undefined;
  const isEdit = locationState?.isEdit || false;
  const facilityId = locationState?.facilityId;
  const facilityName = locationState?.facilityName;

  const [isLoading, setIsLoading] = useState(false);
  const [initialUserData, setInitialUserData] = useState<User | null>(userData || null);
  const [selectedRole, setSelectedRole] = useState<string>(userData?.role || '');
  const [modalityOptions, setModalityOptions] =
    useState<Array<{ value: string; label: string }>>(MODALITY_OPTIONS);

  // Get has_radiologist_hierarchy from auth service
  const hasRadiologistHierarchy = authService.getHasRadiologistHierarchy();

  // Load modalities from localStorage on mount
  useEffect(() => {
    const loadModalities = () => {
      try {
        const modalities = getModalities();
        if (modalities && Array.isArray(modalities)) {
          // Transform API response to dropdown options format
          const options = modalities.map(modality => ({
            value: modality.name,
            label: modality.name,
          }));
          setModalityOptions(options);
        }
      } catch (error) {
        console.error('Error loading modalities from storage:', error);
        // Fallback to default MODALITY_OPTIONS if storage fails
      }
    };

    loadModalities();
  }, []);

  // Fetch user data if we have user ID but no user data
  useEffect(() => {
    const fetchUser = async () => {
      if (userData || !isEdit || !locationState?.user) {
        return;
      }

      // Try to get user ID from location state
      const userId = (locationState.user as { id?: number | string })?.id;
      if (!userId) {
        return;
      }

      setIsLoading(true);
      const response = await userService.getUserById(userId);

      if (response.success && response.data) {
        setInitialUserData(response.data);
        setSelectedRole(response.data.role || '');
      } else {
        console.error('Failed to fetch user:', response.error);
        if (uiNotificationService) {
          uiNotificationService.show({
            title: 'Error',
            message: response.error || 'Failed to load user data',
            type: 'error',
            duration: 5000,
          });
        }
      }

      setIsLoading(false);
    };

    fetchUser();
  }, [isEdit, locationState, userData, uiNotificationService]);

  const initialValues = mapUserToInitialValues(initialUserData, selectedRole);

  // Get validation schema based on role
  const getValidationSchema = () => {
    return getUserValidationSchema(selectedRole, hasRadiologistHierarchy ?? undefined);
  };

  const handleCancel = () => {
    // If coming from Facility Management, go back to facility view
    if (facilityId && facilityName) {
      navigate('/facility-view', {
        state: {
          facilityId,
          facilityName,
          activeTab: 'users', // Set Users tab as active
        },
      });
    } else {
      // Otherwise, go back to User Management
      navigate('/users');
    }
  };

  // Generate title based on context
  const getTitle = () => {
    const baseTitle = isEdit ? 'Edit User' : 'Add New User';
    // If coming from Facility Management, show breadcrumb
    if (facilityId && facilityName) {
      return `Facility Management | ${facilityName} | ${baseTitle}`;
    }
    return baseTitle;
  };

  const handleSubmit = async (rawValues: typeof initialValues) => {
    const values = trimFormStringFields(rawValues as Record<string, unknown>) as AddUserFormValues;
    try {
      setIsLoading(true);

      if (!validateRadiologistSignatureUpload(values, uiNotificationService)) {
        setIsLoading(false);
        return;
      }

      const currentUserRole = resolveCurrentUserRoleForMapper();
      const facilityIdForRequest = resolveAddUserFacilityId(values.role, locationState?.facilityId);

      const formData = await mapFormValuesToCreateRequest(
        values as any,
        facilityIdForRequest,
        isEdit,
        currentUserRole
      );

      const response =
        isEdit && initialUserData?.id
          ? await userService.updateUser(initialUserData.id, formData)
          : await userService.createUser(formData);

      if (response.success) {
        notifyUserForm(
          uiNotificationService,
          true,
          response.message ||
            (isEdit && initialUserData?.id
              ? 'User updated successfully'
              : 'User created successfully'),
          ''
        );
        navigateAfterUserSave(navigate, facilityIdForRequest, facilityName);
        return;
      }

      console.error(
        isEdit && initialUserData?.id ? 'Failed to update user:' : 'Failed to create user:',
        response.error
      );
      notifyUserForm(
        uiNotificationService,
        false,
        '',
        response.error ||
          (isEdit && initialUserData?.id ? 'Failed to update user' : 'Failed to create user')
      );
    } catch (error) {
      console.error('Error submitting user form:', error);
      notifyUserForm(
        uiNotificationService,
        false,
        '',
        error instanceof Error ? error.message : 'An unexpected error occurred'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const roleOptions = [
    { value: 'Technician', label: 'Technician' },
    { value: 'Radiologist', label: 'Radiologist' },
  ];

  const subRoleOptions = [
    { value: 'Junior', label: 'Junior' },
    { value: 'Senior', label: 'Senior' },
  ];

  const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
  ];

  const availabilityHoursOptions = [
    { value: '10:00 AM - 12:00 PM', label: '10:00 AM - 12:00 PM' },
    { value: '02:00 PM - 04:00 PM', label: '02:00 PM - 04:00 PM' },
    { value: '05:00 AM - 07:00 PM', label: '05:00 AM - 07:00 PM' },
  ];

  const availabilityDaysOptions = [
    { value: 'monday', label: 'M' },
    { value: 'tuesday', label: 'T' },
    { value: 'wednesday', label: 'W' },
    { value: 'thursday', label: 'T' },
    { value: 'friday', label: 'F' },
    { value: 'saturday', label: 'S' },
    { value: 'sunday', label: 'S' },
  ];

  if (isLoading && isEdit && !initialUserData) {
    return (
      <PageLayout
        title={getTitle()}
        onBack={handleCancel}
      >
        <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-secondary)' }}>
          Loading user data...
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title={getTitle()}
      onBack={handleCancel}
    >
      <Formik
        initialValues={initialValues}
        validationSchema={getValidationSchema()}
        onSubmit={handleSubmit}
        enableReinitialize={true}
        validateOnMount={true}
        validateOnChange={true}
        validateOnBlur={true}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          setFieldValue,
          setFieldError,
          setFieldTouched,
          handleSubmit,
          isValid,
          dirty,
        }) => {
          const trimTextFieldBlur =
            (fieldName: keyof AddUserFormValues) => (e: React.FocusEvent<HTMLInputElement>) => {
              handleBlur(e);
              if (fieldName === 'password') {
                return;
              }
              const raw = e.target.value;
              const trimmed = raw.trim();
              if (trimmed !== raw) {
                setFieldValue(String(fieldName), trimmed, false);
              }
            };

          // Helper function to show errors as user types (real-time validation)
          const getFieldError = (fieldName: string) => {
            const error = errors[fieldName as keyof typeof errors];
            const isTouched = touched[fieldName as keyof typeof touched];
            const fieldValue = values[fieldName as keyof typeof values];

            // For array fields, check if array has items (not just truthy)
            const hasValue = Array.isArray(fieldValue)
              ? fieldValue.length > 0
              : fieldValue !== null && fieldValue !== undefined && fieldValue !== '';

            // Show error only if:
            // 1. Field has been touched (user interacted), OR
            // 2. Field has a value (user has entered/selected something)
            // This prevents showing errors on empty fields before user interaction
            return (isTouched || hasValue) && error
              ? typeof error === 'string'
                ? error
                : undefined
              : undefined;
          };
          return (
            <Form>
              <div
                style={{
                  padding: '0',
                  backgroundColor: 'var(--bg-primary)',
                  minHeight: 'calc(100vh - 56px)',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {/* Form Content */}
                <div
                  style={{
                    flex: 1,
                    paddingBottom: '84px',
                  }}
                >
                  {/* Section 1: Role */}
                  <FormSection
                    number={1}
                    title="Role"
                  >
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(4, 1fr)',
                        gap: '16px',
                      }}
                    >
                      <FormSelect
                        label="Role"
                        name="role"
                        required
                        value={values.role}
                        onChange={e => {
                          handleChange(e);
                          setSelectedRole(e.target.value);
                          // Reset sub_role when role changes
                          if (e.target.value !== 'Radiologist') {
                            setFieldValue('sub_role', '');
                          }
                        }}
                        options={roleOptions}
                        placeholder="Select Role"
                        error={touched.role && errors.role ? errors.role : undefined}
                        disabled={isEdit}
                      />
                      {selectedRole === 'Radiologist' && (
                        <FormSelect
                          label="Sub Role"
                          name="sub_role"
                          required
                          value={values.sub_role}
                          onChange={handleChange}
                          options={subRoleOptions}
                          placeholder="Select Sub Role"
                          error={touched.sub_role && errors.sub_role ? errors.sub_role : undefined}
                        />
                      )}
                    </div>
                  </FormSection>

                  {/* Section 2: General Details */}
                  <FormSection
                    number={2}
                    title="General Details"
                  >
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(4, 1fr)',
                        gap: '24px',
                        marginBottom: '24px',
                      }}
                    >
                      <FormField
                        label="Employee ID"
                        name="employeeId"
                        required
                        value={values.employeeId}
                        onChange={handleChange}
                        onBlur={trimTextFieldBlur('employeeId')}
                        placeholder="Enter"
                        error={getFieldError('employeeId')}
                      />
                      <FormField
                        label="Full Name"
                        name="fullName"
                        required
                        value={values.fullName}
                        onChange={handleChange}
                        onBlur={trimTextFieldBlur('fullName')}
                        placeholder="Enter"
                        error={getFieldError('fullName')}
                      />
                      <FormButtonGroup
                        label="Gender"
                        required
                        options={GENDER_OPTIONS}
                        value={values.gender}
                        onChange={value => setFieldValue('gender', value)}
                        error={getFieldError('gender')}
                      />
                      <FormDatePicker
                        label="Date of Birth"
                        required
                        value={values.dateOfBirth}
                        onChange={value => setFieldValue('dateOfBirth', value)}
                        placeholder="Select A Date"
                        error={getFieldError('dateOfBirth')}
                      />
                    </div>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(4, 1fr)',
                        gap: '24px',
                        marginBottom: '24px',
                      }}
                    >
                      <FormField
                        label="Email"
                        name="email"
                        type="email"
                        required
                        value={values.email}
                        onChange={handleChange}
                        onBlur={trimTextFieldBlur('email')}
                        placeholder="Enter"
                        disabled={isEdit}
                        error={getFieldError('email')}
                      />
                      <FormPhoneInput
                        label="Mobile No."
                        required
                        value={values.phoneNo}
                        onChange={value => setFieldValue('phoneNo', value)}
                        placeholder="Enter"
                        error={getFieldError('phoneNo')}
                      />
                      <FormField
                        label="Username"
                        name="username"
                        required
                        value={values.username}
                        onChange={handleChange}
                        onBlur={trimTextFieldBlur('username')}
                        placeholder="Enter"
                        disabled={isEdit}
                        error={getFieldError('username')}
                      />
                      {!isEdit && (
                        <FormField
                          label="Password"
                          name="password"
                          required={!isEdit}
                          type="password"
                          value={values.password}
                          onChange={handleChange}
                          onBlur={trimTextFieldBlur('password')}
                          onFocus={e => {
                            // Clear placeholder when user focuses on the field
                            if (values.password === PASSWORD_PLACEHOLDER) {
                              setFieldValue('password', '');
                            }
                          }}
                          placeholder="Enter Password"
                          error={getFieldError('password')}
                          rightIcon={
                            <Icons.EyeVisible
                              style={{
                                color: '#334155',
                                cursor: 'pointer',
                                width: '20px',
                                height: '20px',
                              }}
                            />
                          }
                        />
                      )}
                    </div>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(4, 1fr)',
                        gap: '24px',
                      }}
                    >
                      <FormRadioGroup
                        label="Status"
                        required
                        options={statusOptions}
                        value={values.status}
                        onChange={value => setFieldValue('status', value)}
                        error={getFieldError('status')}
                      />
                    </div>
                  </FormSection>

                  {/* Section 3: Professional Credentials - Role Specific */}
                  {selectedRole && (
                    <FormSection
                      number={3}
                      title="Professional Credentials"
                    >
                      {selectedRole === 'Technician' && (
                        <div
                          style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(4, 1fr)',
                            gap: '24px',
                          }}
                        >
                          <FormTimePicker
                            label="Shift start at"
                            required
                            value={values.shiftStart}
                            onChange={value => setFieldValue('shiftStart', value)}
                            placeholder="Select start timing"
                            error={getFieldError('shiftStart')}
                          />
                          <FormTimePicker
                            label="Shift end at"
                            required
                            value={values.shiftEnd}
                            onChange={value => setFieldValue('shiftEnd', value)}
                            placeholder="Select end timing"
                            error={getFieldError('shiftEnd')}
                          />
                          <FormField
                            label="Experience"
                            name="experienceYears"
                            inputMode="numeric"
                            autoComplete="off"
                            value={
                              values.experienceYears !== undefined &&
                              values.experienceYears !== null
                                ? String(values.experienceYears)
                                : ''
                            }
                            onChange={e => {
                              const digits = e.target.value.replace(/\D/g, '').slice(0, 3);
                              const numValue = digits === '' ? undefined : parseInt(digits, 10);
                              setFieldValue('experienceYears', numValue, false);
                            }}
                            onBlur={handleBlur}
                            placeholder="Enter Years of Experience"
                            error={getFieldError('experienceYears')}
                          />
                          <div></div>
                        </div>
                      )}

                      {selectedRole === 'Radiologist' && (
                        <>
                          <div
                            style={{
                              display: 'grid',
                              gridTemplateColumns: 'repeat(4, 1fr)',
                              gap: '24px',
                              marginBottom: '24px',
                            }}
                          >
                            <FormField
                              label="Registration No."
                              name="registrationNo"
                              required
                              value={values.registrationNo}
                              onChange={handleChange}
                              onBlur={trimTextFieldBlur('registrationNo')}
                              placeholder="Enter Registration No."
                              error={getFieldError('registrationNo')}
                            />
                            <FormField
                              label="Qualification"
                              name="qualification"
                              required
                              value={values.qualification}
                              onChange={handleChange}
                              onBlur={trimTextFieldBlur('qualification')}
                              placeholder="Enter"
                              error={getFieldError('qualification')}
                            />
                            <FormField
                              label="Experience"
                              name="experienceYears"
                              inputMode="numeric"
                              autoComplete="off"
                              value={
                                values.experienceYears !== undefined &&
                                values.experienceYears !== null
                                  ? String(values.experienceYears)
                                  : ''
                              }
                              onChange={e => {
                                const digits = e.target.value.replace(/\D/g, '').slice(0, 3);
                                const numValue = digits === '' ? undefined : parseInt(digits, 10);
                                setFieldValue('experienceYears', numValue, false);
                              }}
                              onBlur={handleBlur}
                              placeholder="Enter Years of Experience"
                              error={getFieldError('experienceYears')}
                            />
                            <FormField
                              label="Specialty"
                              name="specialty"
                              value={values.specialty}
                              onChange={handleChange}
                              onBlur={trimTextFieldBlur('specialty')}
                              placeholder="Enter Specialty"
                            />
                          </div>
                          <div
                            style={{
                              display: 'grid',
                              gridTemplateColumns: 'repeat(4, 1fr)',
                              gap: '24px',
                              marginBottom: '24px',
                            }}
                          >
                            <FormField
                              label="Designation"
                              name="designation"
                              value={values.designation}
                              onChange={handleChange}
                              onBlur={trimTextFieldBlur('designation')}
                              placeholder="Enter Designation"
                            />
                            <FormMultiSelect
                              label="Reporting Modality Access"
                              required
                              options={modalityOptions}
                              value={values.reportingModalityAccess || []}
                              onChange={value => {
                                setFieldValue('reportingModalityAccess', value);
                              }}
                              placeholder="Select Modality Access"
                              error={getFieldError('reportingModalityAccess')}
                            />
                            <FileUpload
                              label="Signature"
                              value={values.signature?.name || values.signatureFileName || ''}
                              onChange={file => {
                                if (file) {
                                  setFieldError('signature', undefined);
                                  setFieldValue('signatureFileName', '');
                                }
                                setFieldValue('signature', file);
                              }}
                              accept={SIGNATURE_UPLOAD_ACCEPT}
                              validateSelectedFile={file =>
                                isAllowedSignatureUploadFile(file)
                                  ? undefined
                                  : SIGNATURE_UPLOAD_TYPE_ERROR
                              }
                              onValidationError={message => {
                                setFieldError('signature', message);
                                setFieldTouched('signature', true);
                              }}
                              required
                              error={getFieldError('signature')}
                            />
                            <FormMultiSelect
                              label="Availability Hours"
                              options={availabilityHoursOptions}
                              value={
                                Array.isArray(values.availabilityHours)
                                  ? values.availabilityHours
                                  : values.availabilityHours
                                    ? [values.availabilityHours]
                                    : []
                              }
                              onChange={value => setFieldValue('availabilityHours', value)}
                              placeholder="Select Availability Hours"
                            />
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '4px',
                              }}
                            >
                              <label
                                style={{
                                  fontSize: '14px',
                                  fontWeight: 500,
                                  color: 'var(--label-color-default)',
                                  lineHeight: '20px',
                                }}
                              >
                                Availability Days
                              </label>
                              <div
                                style={{
                                  display: 'flex',
                                  gap: '12px',
                                  flexWrap: 'wrap',
                                }}
                              >
                                {availabilityDaysOptions.map(day => (
                                  <button
                                    key={day.value}
                                    type="button"
                                    onClick={() => {
                                      const currentDays = values.availabilityDays || [];
                                      const isSelected = currentDays.includes(day.value);
                                      if (isSelected) {
                                        setFieldValue(
                                          'availabilityDays',
                                          withoutAvailabilityDay(currentDays, day.value)
                                        );
                                      } else {
                                        setFieldValue('availabilityDays', [
                                          ...currentDays,
                                          day.value,
                                        ]);
                                      }
                                    }}
                                    style={{
                                      width: '36px',
                                      height: '36px',
                                      borderRadius: '9999px',
                                      border: (values.availabilityDays || []).includes(day.value)
                                        ? '1px solid #2563EB' // Dark blue border for selected days
                                        : '1px solid #334155', // Light gray border for unselected days
                                      backgroundColor: (values.availabilityDays || []).includes(
                                        day.value
                                      )
                                        ? '#1E3A8A' // Dark blue background for selected days
                                        : '#020617', // White background for unselected days
                                      color: (values.availabilityDays || []).includes(day.value)
                                        ? '#93C5FD' // White text for selected days
                                        : '#E2E8F0', // Dark text for unselected days
                                      fontSize: '14px',
                                      fontWeight: 500,
                                      cursor: 'pointer',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      transition:
                                        'background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease',
                                    }}
                                  >
                                    {day.label}
                                  </button>
                                ))}
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                    </FormSection>
                  )}
                </div>

                {/* Footer with Action Buttons */}
                <div
                  style={{
                    position: 'fixed',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: '84px',
                    padding: '16px',
                    backgroundColor: 'var(--bg-primary)',
                    borderTop: '1px solid var(--border-primary)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    gap: '8px',
                    zIndex: 100,
                  }}
                >
                  <button
                    type="button"
                    onClick={handleCancel}
                    disabled={isLoading}
                    className="btn btn-secondary btn-sm btn-inline"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading || (isEdit && !dirty)}
                    onClick={e => {
                      if (isEdit && !dirty) {
                        e.preventDefault();
                        // Check for special cases: file uploads (signature)
                        const hasFileChange =
                          values.signature instanceof File ||
                          (values.signatureFileName &&
                            values.signatureFileName !== initialValues.signatureFileName);

                        if (!hasFileChange) {
                          // No changes detected
                          if (uiNotificationService) {
                            uiNotificationService.show({
                              title: 'No Changes',
                              message: 'No changes were made to the user details',
                              type: 'info',
                              duration: 3000,
                            });
                          }
                          return;
                        }
                      }
                    }}
                    className="btn btn-primary btn-sm btn-inline"
                  >
                    {isLoading ? 'Processing...' : isEdit ? 'Update User' : 'Add User'}
                  </button>
                </div>
              </div>
            </Form>
          );
        }}
      </Formik>
    </PageLayout>
  );
};

export default AddUser;
